import React, {useState, useEffect, useRef} from 'react'
import {Col, Row, Block} from 'jsxstyle'
import {ReactComponent as ThreeDotsIcon} from '../../common/assets/three-dots-vertical.svg'

export interface ActionItem {
  label: string
  onClick: () => void
  icon?: JSX.Element
  disable?: boolean
}

interface ActionsDropdownProps {
  actions: ActionItem[]
  width?: string
  hoverBackgroundColor?: string
  borderColor?: string
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({actions, width = '40px', hoverBackgroundColor = '#F3F3F3', borderColor = '#ccc'}) => {
  const [open, setOpen] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [animate, setAnimate] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setShowDropdown(true)
      const timeout = setTimeout(() => setAnimate(true), 10)
      return () => clearTimeout(timeout)
    } else {
      setAnimate(false)
      const timeout = setTimeout(() => setShowDropdown(false), 200)
      return () => clearTimeout(timeout)
    }
  }, [open])

  actions = actions.filter((action) => !action.disable)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleActionClick = (action: ActionItem) => {
    action.onClick()
    setOpen(false)
  }

  return (
    <Col position="relative" width={width}>
      <Row
        alignItems="center"
        justifyContent="center"
        padding="8px"
        border={`1px solid ${borderColor}`}
        borderRadius="6px"
        backgroundColor="#FAFAFA"
        cursor="pointer"
        userSelect="none"
        hoverBackgroundColor={hoverBackgroundColor}
        transition="background-color 0.2s ease"
        props={{
          onClick: () => setOpen((prev) => !prev),
        }}
      >
        <ThreeDotsIcon width={16} height={16} />
      </Row>

      {showDropdown && (
        <Col
          position="absolute"
          top="calc(100% + 4px)"
          right="0"
          minWidth="120px"
          border="1px solid #E5E5E5"
          borderRadius="6px"
          backgroundColor="#FAFAFA"
          maxHeight="300px"
          overflowY="auto"
          zIndex={999}
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
          style={{
            opacity: animate ? 1 : 0,
            transform: animate ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}
          props={{ref: dropdownRef}}
        >
          {actions.map((action, index) => (
            <Row
              key={index}
              padding="8px 12px"
              gap="8px"
              cursor="pointer"
              hoverBackgroundColor="#F3F3F3"
              transition="background-color 0.2s ease"
              alignItems="center"
              props={{
                onClick: () => handleActionClick(action),
              }}
            >
              {action.icon && <Block>{action.icon}</Block>}
              <Block>{action.label}</Block>
            </Row>
          ))}
        </Col>
      )}
    </Col>
  )
}

export default ActionsDropdown
